import { Component, type ErrorInfo, type ReactNode } from "react";

interface Props {
  children: ReactNode;
}

interface State {
  error: Error | null;
}

/**
 * Catches render errors so a bug shows an explanation and a way out rather than
 * a blank page.
 *
 * Reloading is offered because the reader's work is already in IndexedDB — the
 * persistence layer flushes on `pagehide` — so a reload resumes where they were.
 */
class ErrorBoundary extends Component<Props, State> {
  state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  componentDidCatch(error: Error, info: ErrorInfo): void {
    console.error("Unhandled error in the erasure UI:", error, info.componentStack);
  }

  render(): ReactNode {
    const { error } = this.state;
    if (!error) return this.props.children;

    return (
      <div
        role="alert"
        style={{
          maxWidth: "480px",
          margin: "15vh auto",
          padding: "0 24px",
          textAlign: "center",
          fontFamily: "Helvetica, Arial, sans-serif",
        }}
      >
        <h1 style={{ fontSize: "24px" }}>Something broke.</h1>
        <p style={{ color: "#555" }}>
          Your saved erasures are safe. Reloading should bring you back to where you were.
        </p>
        <button
          type="button"
          onClick={() => window.location.reload()}
          style={{
            padding: "10px 20px",
            fontSize: "16px",
            cursor: "pointer",
            background: "black",
            color: "white",
            border: "none",
            borderRadius: "4px",
          }}
        >
          Reload
        </button>
        <pre
          style={{
            marginTop: "24px",
            textAlign: "left",
            whiteSpace: "pre-wrap",
            wordBreak: "break-word",
            color: "#777",
            fontSize: "12px",
          }}
        >
          {error.message}
        </pre>
      </div>
    );
  }
}

export default ErrorBoundary;
